import { useState } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// MRR model: 58 EUR/mes/suscriptor, suscriptores acumulan, churn ~5%/mes
// Base: 180 nuevos suscriptores/mes (3000 leads x 6%), ramp-up 3 meses
const projections = [
  { month: 'Abr', conservador: 2610, base: 5220, optimista: 7830 },
  { month: 'May', conservador: 5000, base: 10150, optimista: 15500 },
  { month: 'Jun', conservador: 7200, base: 14800, optimista: 22800 },
  { month: 'Jul', conservador: 9200, base: 19200, optimista: 29700 },
  { month: 'Ago', conservador: 11000, base: 23300, optimista: 36200 },
  { month: 'Sep', conservador: 12700, base: 27100, optimista: 42300 },
  { month: 'Oct', conservador: 14200, base: 30600, optimista: 48000 },
  { month: 'Nov', conservador: 15600, base: 33900, optimista: 53400 },
  { month: 'Dic', conservador: 16800, base: 36900, optimista: 58400 },
  { month: 'Ene 27', conservador: 17900, base: 39700, optimista: 63100 },
  { month: 'Feb', conservador: 18900, base: 42300, optimista: 67500 },
  { month: 'Mar 27', conservador: 19800, base: 44700, optimista: 71600 },
];

const scenarios = [
  { key: 'optimista', label: 'Optimista', color: '#3b82f6', mrr12: '71.6K', arr: '859K', desc: 'Conversion 8%, leads a 1.5 EUR' },
  { key: 'base', label: 'Base', color: '#10b981', mrr12: '44.7K', arr: '536K', desc: '6% conversion, 58 EUR/mes, 5% churn' },
  { key: 'conservador', label: 'Conservador', color: '#f97316', mrr12: '19.8K', arr: '237K', desc: '4% conversion, ramp-up lento' },
] as const;

export function FinancialProjections() {
  const [activeScenarios, setActiveScenarios] = useState<Set<string>>(new Set(['base', 'optimista', 'conservador']));

  const toggle = (key: string) => {
    setActiveScenarios(prev => {
      const next = new Set(prev);
      if (next.has(key) && next.size > 1) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section id="projections" className="py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-emerald-400">
            Proyecciones reales con modelo de suscripcion
          </span>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            MRR que{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">
              crece cada mes
            </span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-400">
            Cada cliente que se suscribe paga 58 EUR/mes durante 10 meses. Los suscriptores se acumulan.
            Con 205K EUR de inversion, el MRR crece de forma compuesta mes a mes.
          </p>
        </motion.div>

        {/* Key facts */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-8 grid gap-3 sm:grid-cols-5"
        >
          {[
            { label: 'Inversion', value: '205K', desc: 'Para 12 meses', color: 'text-orange-400' },
            { label: 'Suscripcion', value: '58 EUR/mes', desc: 'Media ponderada', color: 'text-emerald-400' },
            { label: 'Nuevos/mes', value: '180', desc: '3K leads x 6%', color: 'text-blue-400' },
            { label: 'LTV', value: '580 EUR', desc: '58 x 10 meses', color: 'text-purple-400' },
            { label: 'CAC', value: '33 EUR', desc: '2 EUR lead / 6%', color: 'text-orange-400' },
          ].map((item, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.06] p-3 text-center backdrop-blur-md">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{item.label}</p>
              <p className={`mt-0.5 text-lg font-bold ${item.color}`}>{item.value}</p>
              <p className="text-[10px] text-slate-500">{item.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Scenario toggles */}
        <div className="mb-6 flex flex-wrap justify-center gap-3">
          {scenarios.map(s => (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                activeScenarios.has(s.key)
                  ? 'bg-white/10 text-white ring-1 ring-white/20'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color, opacity: activeScenarios.has(s.key) ? 1 : 0.3 }} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-md"
        >
          <ResponsiveContainer width="100%" height={380}>
            <AreaChart data={projections} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <defs>
                {scenarios.map(s => (
                  <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <YAxis
                stroke="#94a3b8"
                tick={{ fontSize: 11 }}
                tickFormatter={v => `${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  color: '#e2e8f0',
                  fontSize: 13,
                }}
                formatter={(val) => [`${Number(val).toLocaleString('es-ES')} EUR/mes`]}
              />
              {scenarios.map(s =>
                activeScenarios.has(s.key) ? (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={2}
                    fill={`url(#grad-${s.key})`}
                    dot={{ r: 3, fill: s.color }}
                    activeDot={{ r: 5 }}
                  />
                ) : null
              )}
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Summary cards */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {scenarios.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * i }}
              className="rounded-xl border border-white/10 bg-white/[0.06] p-4 text-center backdrop-blur-md"
            >
              <p className="text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
              <p className="mt-1 text-2xl font-bold" style={{ color: s.color }}>
                MRR {s.mrr12}
              </p>
              <p className="text-xs text-slate-400">ARR: {s.arr} EUR</p>
              <p className="mt-1 text-[10px] text-slate-500">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
